import { observer } from "mobx-react"; // Importing MobX observer for state management
import moment from "moment";
import { useEffect } from "react"; // Importing React hooks
import { Modal } from "react-bootstrap"; // Importing Bootstrap components
import { FormattedMessage } from "react-intl";
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum";
import { BookingTypeEnum } from "../../../../constants/enums/booking-type-enum";
import { CancellationTypeEnum } from "../../../../constants/enums/cancellation-type-enum";
import { paymentType } from "../../../../constants/enums/payment-type-enum";
import { useStore } from "../../../../contexts/store-provider"; // Importing store context
import { getBusinessTypeIdFromLocalStorage, getHeadquarterIdFromLocalStorage } from "../../../../helpers/localstorage-helper";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc"; // Importing delete popup HOC
import RebookReservationFormComponent from "../form-component/rebook-reservation-form-component";
import toast from "react-hot-toast";
import { formatMessage } from "../../../../translations/format-message";

interface IProps {
  appointment: any; // Appointment details
  day: any; // Day information
  staffId: number; // ID of the staff
  onModalClosed: () => void; // Callback when modal is closed
  storeId: number; //StoreId
  viewOnly: boolean; // Flag for view-only mode
}

const RebookReservationComponent = (props: IProps) => {
  const { reservationSchedulerStore, bookingManagementStore } = useStore(); // Accessing store context
  const { AddAppointmentService, addUpdateAppointmentState } =
    bookingManagementStore; // Booking management store

  useEffect(() => {
    if (addUpdateAppointmentState.success) {
      // toast.success(formatMessage("added_success")); // Show success toast for adding break
      onModalClosed(); // Close modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateAppointmentState.success]);

    useEffect(() => {
    if (addUpdateAppointmentState.error) {
      toast.error(formatMessage(addUpdateAppointmentState.error)); // Show error toast for adding break
      onModalClosed(); // Close modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateAppointmentState.error]);

  // Handles form submission for adding or updating staff break details.
  const onSubmitForm = (values: any) => {
    const { totalAmount, totalTaxes } = values.ServiceRequestedVMss?.reduce(
      (acc: { totalAmount: number; totalTaxes: number }, service: any) => {
        const charges = parseFloat(service.Charges) || 0;
        const taxAmount = parseFloat(service.TaxAmount) || 0;
        
        return {
          totalAmount: acc.totalAmount + charges,
          totalTaxes: acc.totalTaxes + taxAmount,
        };
      },
      { totalAmount: 0, totalTaxes: 0 }
    ) || { totalAmount: 0, totalTaxes: 0 };
    let headquarterId = getHeadquarterIdFromLocalStorage();
    let obj = {
      CustomerId: values.CustomerId,
      StoreId: values.StoreId,
      StaffPreference: 0,
      RequestedStaffId: null,
      AssignedStaffId: values.AssignedStaffId,
      Date: `${moment(values?.Date)?.format("YYYY-MM-DD")}T00:00:00.000Z`, // Set appointment date, // Format date to YYYY-MM-DD
      Time: `${values.Time}:00`, // Time of the appointment
      AppointmentStatus: bookingStatusEnum.Reserved,
      PaymentType: paymentType.OnSitePayment,
      NominationFee: 0,
      TotalTime: values.TotalTime, // Total time for the appointment
      TotalAmount: totalAmount, // Total amount for the appointment
      TotalTaxes: totalTaxes, // Total taxes for the appointment
      ConsumptionTax: values.ConsumptionTax,
      CancelationType: CancellationTypeEnum.None,
      BookingType: BookingTypeEnum.NewVisit,
      ReservationMemo: "",
      PostBreakTime: values.PostBreakTime?.toString(), // Post break time
      BusinessTypeId: getBusinessTypeIdFromLocalStorage(), // Get business type ID from local storage
      HeadquarterId: 
       Number(headquarterId) > 0 ? Number(headquarterId) : null,
      Service: values.ServiceRequestedVMss, // Service details
    };

    AddAppointmentService(obj); // Call service to add appointment
  };

  // Handles modal close event and resets the staff break state
  const onModalClosed = () => {
    // resetGetStaffBreakByIdState(); // Reset state for fetching staff break by ID
    props.onModalClosed(); // Call parent modal close handler
  };

  return (
    <Modal show={true} centered size="sm" className="pb-0 bg-transparent-black">
      {" "}
      {/* Modal for adding/updating break */}
      <Modal.Header>
        <Modal.Title>
          {<FormattedMessage id="rebook_reservation" />}
          <button
            onClick={onModalClosed} // Close modal on button click
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />{" "}
              {/* Close icon */}
            </svg>
          </button>
        </Modal.Title>{" "}
      </Modal.Header>
      <RebookReservationFormComponent
        // id={props.id} // Pass break ID
        initialValues={props.appointment} // Pass initial form values
        day={props.day} // Pass day information
        onModalClosed={onModalClosed} // Pass modal close callback
        submitHandler={onSubmitForm} // Pass form submit handler
        isLoading={
          addUpdateAppointmentState.inProgress // Check if add operation is in progress
        }
        viewOnly={props.viewOnly} // Pass view-only flag
      />
    </Modal>
  );
};

export default DeletePopupHOC(observer(RebookReservationComponent)); // Wrap component with MobX observer and delete popup HOC
